import { Component, Input, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { DAO } from '../modal/DAO';
import { QuestionAnswer, Questionnaire } from '../modal/questionnaire.models';
import { ServiceLayer } from '../services/serviceLayer';
import { MessagesModule } from 'primeng/messages';

@Component({
  selector: 'vin-validation-warning-message',
  template: ` 
    @if (!showCorrectedVinMessageOnly && showIndicationWarning) { 
      <p-messages 
        [(value)]="premiumIndicationMessages" 
        [closable]="false"> 
      </p-messages> 
    }
    @if (showCorrectedVinMessageOnly && showCorrectedVinInformation) { 
      <p-messages 
        [(value)]="correctedVinInformationMessages" 
        [closable]="false"> 
      </p-messages> 
    }
  `,
  styles: [
    ` 
      .p-message .p-message-text { 
        font-weight: normal !important; 
        white-space: pre-line; 
      } 
    `
  ],
  standalone: true,
  imports: [MessagesModule],
  encapsulation: ViewEncapsulation.None
})

export class VinValidationWarningMessageComponent {

  public premiumIndicationMessages: any[] = [];
  private oldWarningMsg: string = "";

  public correctedVinInformationMessages: any[] = [];
  private oldCorrectedVinInformationMsg: string = "";

  @Input() showCorrectedVinMessageOnly: boolean = false;

  constructor(private dao: DAO, private serviceLayer: ServiceLayer) { }

  public get showIndicationWarning(): boolean {
    const currentPage: string = this.dao.PolicyTemplate;
    let invalidVins: string[] = [];

    // For UnitInfo page, check VIN status from the vehicle model 
    if (currentPage === 'vehicles') {
      if (!this.serviceLayer.isIndicationApplicable(this.dao?.DOTList?.accountState, this.dao?.vehicleModel?.VehiclesList?.length)) {
        return false;
      }

      // Collect invalid powerunit VINs 
      this.dao.vehicleModel.VehiclesList?.forEach((v: any) => {
        if (v.vehicleVin.status === 'checked.invalid' && v.vehicleVin.answer) {
          invalidVins.push(v.vehicleVin.answer);
        }
      });
    }

    // For DriverInfo and ProductInfo, check VIN status from vehiclePrefillQsnr
    if (currentPage === 'driver' || currentPage === 'product') {
      const vehicleQsnr: Questionnaire = this.dao?.DOTList?.vehiclePrefillQsnr;
      if (!vehicleQsnr) {
        return false;
      }
      const vehVinQAs: Array<QuestionAnswer> = vehicleQsnr.findQuestions('vehicleVin');
      const puLength: number = vehVinQAs.filter((x) => x.group.includes('VEH')).length;

      // Exit early if premium indication is not available 
      if (!this.serviceLayer.isIndicationApplicable(this.dao.DOTList.accountState, puLength)) {
        return false;
      }

      const puVinQAs: Array<QuestionAnswer> = vehVinQAs.filter((x) => x.group.includes('VEH'));
      invalidVins = puVinQAs.filter((x) => x.status === 'checked.invalid').map((x) => x.answer);
    }

    if (invalidVins.length == 0) return false;

    this.resetWarningMessage(invalidVins);
    return true;
  }

  private resetWarningMessage(invalidVins: string[]): void {
    const joined = invalidVins.join(', ');
    const message = `The VIN number(s) could not be located using our available services. As a result, an online indication for this submission is not available.  
      Invalid VIN(s): ${joined}.`;

    if (this.oldWarningMsg == message) return;

    this.oldWarningMsg = message;
    this.premiumIndicationMessages = [{
      severity: 'warn',
      summary: message
    }];
  }

  public get showCorrectedVinInformation(): boolean {
    const currentPage: string = this.dao.PolicyTemplate;
    let incorrectAndCorrectedVins: Array<{ incorrectVin: string; correctedVin: string }> = [];

    if (currentPage === 'vehicles') {
      if (!this.serviceLayer.isIndicationApplicable(this.dao?.DOTList?.accountState, this.dao?.vehicleModel?.VehiclesList?.length)) {
        return false;
      }

      // Collect user entered(incorrect) vin and Rlpolk's corrected vin (only for powerunits)
      this.dao.vehicleModel.VehiclesList?.filter(veh => veh.vehicleVin).forEach((v: any) => {
        if (!!v.vehicleVin.status && v.vehicleVin.status.includes('checked.corrected')) {
          // ex: vehicleVin.status = "checked.corrected: 1234567887654"
          let correctedvin: string = v.vehicleVin.status.split(":")[1]?.trim();

          // if the user-entered (incorrect) VIN is replaced with corrected VIN, then do not show the VIN in the message
          if (correctedvin != v.vehicleVin.answer?.trim()) {
            incorrectAndCorrectedVins.push({
              incorrectVin: v.vehicleVin.answer,
              correctedVin: correctedvin
            });
          }
        }
      });
    }

    if (incorrectAndCorrectedVins.length == 0) return false;

    this.resetCorrectedvinInformationMessage(incorrectAndCorrectedVins);
    return true;
  }

  private resetCorrectedvinInformationMessage(incorrectAndCorrectedVins: Array<{ incorrectVin: string; correctedVin: string }>): void {
    let message = `The following VIN number(s) will be corrected automatically once you click on “Save and Continue”`;
    incorrectAndCorrectedVins.forEach(pair => {
      message += `\n Incorrect VIN (‘${pair.incorrectVin}’) – Corrected VIN (‘${pair.correctedVin}’)`;
    });

    if (this.oldCorrectedVinInformationMsg == message) return;

    this.oldCorrectedVinInformationMsg = message;
    this.correctedVinInformationMessages = [{
      severity: 'info',
      summary: message
    }];
  }
}